<script setup>
import { ref, onMounted, onUnmounted, computed } from "vue";
import { useRouter } from "vue-router";
import { useTheme } from "@/composables/useTheme";
import useCurrentStation from "@/composables/useCurrentStation";
import { usePlayerStore } from "@/stores/player";
import { fetchNowPlaying } from "@/services/api";
import { getCurrentShow } from "@/services/currentShow";
import { formatRange } from "@/utils/timeUtils";
import GooglePlayIcon from "@/components/icons/GooglePlayIcon.vue";
import AppleIcon from "@/components/icons/AppleIcon.vue";
import coverLight from "@/assets/cover-light.png";
import coverDark from "@/assets/cover-dark.png";

// Obtener estación actual
const { currentStationId } = useCurrentStation();
const playerStore = usePlayerStore();
const router = useRouter();

const isMenuOpen = ref(false);
const isMenuClosing = ref(false);
const isLightTheme = ref(
  typeof document !== "undefined" &&
    document.documentElement.classList.contains("light")
);
const nowPlaying = ref(null);
const currentShow = ref(null);
const MOBILE_MENU_ANIMATION_MS = 320;
let closeAnimationTimeout;
let nowPlayingInterval;

// Logo de marca según el tema (claro = A negra, oscuro = A blanca)
const logoSrc = computed(() => (isLightTheme.value ? coverLight : coverDark));

// Composable para temas
const { toggleTheme } = useTheme();

// Computed para el player inline
const isPlaying = computed(() => playerStore.isPlaying);

// Computed: URLs dinámicas
const showsUrl = computed(() => `/shows`);
const tvUrl = computed(() => `/tv`);

// Sincroniza el estado del tema al montar
let observer;
onMounted(() => {
  const html = document.documentElement;
  isLightTheme.value = html.classList.contains("light");

  // (opcional pero recomendable) si el tema cambia desde otro lugar, mantén el header sincronizado
  observer = new MutationObserver(() => {
    isLightTheme.value = html.classList.contains("light");
  });
  observer.observe(html, { attributes: true, attributeFilter: ["class"] });

  // Initialize now playing
  updateNowPlaying();

  // Update now playing every 30 seconds
  nowPlayingInterval = setInterval(updateNowPlaying, 30000);
});

onUnmounted(() => {
  observer?.disconnect();
  if (closeAnimationTimeout) {
    clearTimeout(closeAnimationTimeout);
  }
  if (nowPlayingInterval) {
    clearInterval(nowPlayingInterval);
  }
});

function toggleAndSyncTheme() {
  // Actualiza el estado local para respuesta inmediata
  isLightTheme.value = !isLightTheme.value;
  // Actualiza <html> con tu composable
  toggleTheme();
}

// Player functions
function togglePlay() {
  if (playerStore.isPlaying) {
    playerStore.stop();
  } else {
    playerStore.play();
  }
}

async function updateNowPlaying() {
  try {
    nowPlaying.value = await fetchNowPlaying(currentStationId.value);
    currentShow.value = await getCurrentShow(null, currentStationId.value);
  } catch (error) {
    console.error("Error fetching now playing:", error);
  }
}

// Navegar al detalle del show
function goToShowDetail() {
  if (!currentShow.value) return;

  const showId = currentShow.value.name.toLowerCase().replace(/\s+/g, '-');
  router.push({
    name: 'show-detail',
    params: { id: showId },
  });
}

// Menú
function openMenu() {
  if (closeAnimationTimeout) {
    clearTimeout(closeAnimationTimeout);
    closeAnimationTimeout = null;
  }
  isMenuClosing.value = false;
  isMenuOpen.value = true;
}
function closeMenu() {
  if (!isMenuOpen.value) {
    return;
  }

  isMenuOpen.value = false;
  isMenuClosing.value = true;

  if (closeAnimationTimeout) {
    clearTimeout(closeAnimationTimeout);
  }

  closeAnimationTimeout = setTimeout(() => {
    isMenuClosing.value = false;
    closeAnimationTimeout = null;
  }, MOBILE_MENU_ANIMATION_MS);
}
function closeMenuOnMobile() {
  if (window.innerWidth < 768) closeMenu();
}
</script>

<template>
  <div class="">
    <!-- ═══════════════════════════════════════════════════════════
         BRUTALIST NAVBAR
         ═══════════════════════════════════════════════════════════ -->
    <nav class="navbar">
      <!-- Logo ASTRA -->
      <router-link to="/" class="navbar__logo" title="Astra FM" aria-label="Astra FM — Inicio">
        <img :src="logoSrc" alt="Astra FM" class="navbar__logo-mark" />
      </router-link>

      <!-- Navigation Links (Desktop) -->
      <ul class="navbar__links">
        <li>
          <router-link
            to="/"
            exact-active-class="active-link"
            class="navbar__link"
            title="Home"
          >
            Home
          </router-link>
        </li>
        <li>
          <router-link
            to="/radio"
            exact-active-class="active-link"
            class="navbar__link"
            title="Radio"
          >
            Radio
          </router-link>
        </li>
        <li>
          <router-link
            to="/actualidad"
            exact-active-class="active-link"
            class="navbar__link"
            title="Actualidad"
          >
            Actualidad
          </router-link>
        </li>
        <li>
          <router-link
            :to="showsUrl"
            exact-active-class="active-link"
            class="navbar__link"
            title="Shows"
          >
            Shows
          </router-link>
        </li>
        <li>
          <router-link
            :to="tvUrl"
            exact-active-class="active-link"
            class="navbar__link"
            title="TV"
          >
            TV
          </router-link>
        </li>
        <li>
          <router-link
            to="/about"
            exact-active-class="active-link"
            class="navbar__link"
            title="Sobre Astra FM"
          >
            Sobre Astra FM
          </router-link>
        </li>
        <li class="navbar__app-cta">
          <router-link
            to="/test-app"
            class="navbar__store-btn navbar__store-btn--ios"
            title="Astra para iPhone"
            aria-label="Descarga Astra para iPhone"
          >
            <AppleIcon />
          </router-link>
          <a
            href="https://play.google.com/store/apps/details?id=fm.astra.app"
            target="_blank"
            rel="noopener noreferrer"
            class="navbar__store-btn navbar__store-btn--android"
            title="Descarga Astra en Google Play"
            aria-label="Descarga Astra en Google Play"
          >
            <GooglePlayIcon />
          </a>
        </li>
      </ul>

      <!-- Player Inline -->
      <div class="navbar__player">
        <button
          class="navbar__play-btn"
          :class="{ playing: isPlaying }"
          @click="togglePlay"
          :title="isPlaying ? 'Pause' : 'Play'"
        >
          <i :class="isPlaying ? 'fa-solid fa-pause' : 'fa-solid fa-play'"></i>
        </button>
        <div
          class="navbar__info"
          @click="goToShowDetail"
          :class="{ clickable: currentShow }"
        >
          <span class="navbar__live-dot"></span>
          <span class="navbar__live-label">LIVE</span>
          <span class="navbar__show-name">{{ currentShow?.name || 'Astra Radio' }}</span>
          <span class="navbar__show-time">{{ currentShow?.range ? formatRange(currentShow.range) : '' }}</span>
        </div>
        <button
          class="navbar__theme-toggle"
          @click="toggleAndSyncTheme"
          :title="isLightTheme ? 'Modo claro' : 'Modo oscuro'"
        >
          {{ isLightTheme ? "🌙" : "☀️" }}
        </button>
      </div>

      <!-- Mobile Hamburger -->
      <button
        type="button"
        class="navbar__hamburger"
        @click="openMenu"
        title="Menu"
      >
        <i class="fa-solid fa-bars"></i>
      </button>
    </nav>

    <!-- ═══════════════════════════════════════════════════════════
         MOBILE MENU MODAL
         ═══════════════════════════════════════════════════════════ -->
    <div
      :class="{
        'menu-modal': true,
        visible: isMenuOpen || isMenuClosing,
        active: isMenuOpen,
        closing: isMenuClosing,
      }"
      @click.self="closeMenu"
    >
      <button
        type="button"
        class="menu-modal__close"
        @click="closeMenu"
        title="Close Menu"
      >
        <i class="fa-solid fa-xmark"></i>
      </button>

      <ul class="menu-modal__links">
        <li>
          <router-link
            to="/"
            exact-active-class="active-link"
            class="menu-modal__link"
            title="Home"
            @click="closeMenuOnMobile"
          >
            Home
          </router-link>
        </li>
        <li>
          <router-link
            to="/radio"
            exact-active-class="active-link"
            class="menu-modal__link"
            title="Radio"
            @click="closeMenuOnMobile"
          >
            Radio
          </router-link>
        </li>
        <li>
          <router-link
            to="/actualidad"
            exact-active-class="active-link"
            class="menu-modal__link"
            title="Actualidad"
            @click="closeMenuOnMobile"
          >
            Actualidad
          </router-link>
        </li>
        <li>
          <router-link
            :to="showsUrl"
            exact-active-class="active-link"
            class="menu-modal__link"
            title="Shows"
            @click="closeMenuOnMobile"
          >
            Shows
          </router-link>
        </li>
        <li>
          <router-link
            :to="tvUrl"
            exact-active-class="active-link"
            class="menu-modal__link"
            title="TV"
            @click="closeMenuOnMobile"
          >
            TV
          </router-link>
        </li>
        <li>
          <router-link
            to="/about"
            exact-active-class="active-link"
            class="menu-modal__link"
            title="Sobre Astra FM"
            @click="closeMenuOnMobile"
          >
            Sobre Astra FM
          </router-link>
        </li>
        <li class="menu-modal__app-cta">
          <router-link
            to="/test-app"
            class="menu-modal__store-btn menu-modal__store-btn--ios"
            title="Astra para iPhone"
            aria-label="Descarga Astra para iPhone"
            @click="closeMenuOnMobile"
          >
            <AppleIcon />
          </router-link>
          <a
            href="https://play.google.com/store/apps/details?id=fm.astra.app"
            target="_blank"
            rel="noopener noreferrer"
            class="menu-modal__store-btn menu-modal__store-btn--android"
            title="Descarga Astra en Google Play"
            aria-label="Descarga Astra en Google Play"
            @click="closeMenuOnMobile"
          >
            <GooglePlayIcon />
          </a>
        </li>
      </ul>

      <!-- External Links Section -->
      <div class="menu-modal__sections">
        <!-- Escuchar -->
        <div class="menu-modal__section">
          <h3 class="section-title">ESCUCHAR</h3>
          <div class="section-links">
            <a
              href="https://tunein.com/radio/Astra-Riot-s188076/"
              target="_blank"
              rel="noopener noreferrer"
              class="external-link"
            >
              <i class="fa-solid fa-radio"></i>
              <span>TuneIn</span>
            </a>
            <a
              href="https://open.spotify.com/user/ultravoz?si=203d293e2b954039"
              target="_blank"
              rel="noopener noreferrer"
              class="external-link"
            >
              <i class="fa-brands fa-spotify"></i>
              <span>Spotify</span>
            </a>
          </div>
        </div>

        <!-- Síguenos -->
        <div class="menu-modal__section">
          <h3 class="section-title">SÍGUENOS</h3>
          <div class="section-links">
            <a
              href="https://bsky.app/profile/astra-radio.bsky.social"
              target="_blank"
              rel="noopener noreferrer"
              class="external-link"
            >
              <i class="fa-brands fa-bluesky"></i>
              <span>Bluesky</span>
            </a>
            <a
              href="https://www.facebook.com/AstraRadioLab"
              target="_blank"
              rel="noopener noreferrer"
              class="external-link"
            >
              <i class="fa-brands fa-facebook"></i>
              <span>Facebook</span>
            </a>
            <a
              href="https://www.instagram.com/astralab.fm/"
              target="_blank"
              rel="noopener noreferrer"
              class="external-link"
            >
              <i class="fa-brands fa-instagram"></i>
              <span>Instagram</span>
            </a>
          </div>
        </div>
      </div>

      <div class="menu-modal__footer">
        <button
          class="menu-modal__theme-toggle"
          @click="toggleAndSyncTheme"
          :title="isLightTheme ? 'Modo claro' : 'Modo oscuro'"
        >
          {{ isLightTheme ? "🌙 Modo Oscuro" : "☀️ Modo Claro" }}
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/scss/brutalist-header.scss";
</style>
